import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  Switch,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Timer, Play, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { AppLayout } from '@/components/AppLayout';
import { useThemeColors } from '@/lib/useThemeColors';
import { noOutlineStyle, isWeb } from '@/lib/platform';
import { useAlert } from '@/components/AlertDialog';
import { getUserMessage } from '@/lib/errors';
import {
  useRetentionConfig,
  useUpdateRetentionConfig,
  useRetentionRuns,
  useRunRetentionNow,
  parseRunResults,
  type RetentionConfig,
  type RetentionRun,
  type RetentionRunStatus,
} from '@/lib/retention';

/** Editable card for a single retention category. */
function CategoryCard({ config }: { config: RetentionConfig }) {
  const colors = useThemeColors();
  const { success: showSuccess, error: showError, confirmDestructive } = useAlert();
  const update = useUpdateRetentionConfig();

  const [enabled, setEnabled] = useState(config.enabled);
  const [daysText, setDaysText] = useState(String(config.retention_days));

  const parsedDays = Number(daysText);
  const daysValid = /^\d+$/.test(daysText.trim()) && parsedDays > 0;
  const dirty = enabled !== config.enabled || (daysValid && parsedDays !== config.retention_days);

  const handleSave = async () => {
    if (!daysValid) {
      showError('Invalid retention window', 'Retention days must be a whole number greater than 0.');
      return;
    }
    if (enabled && !config.enabled) {
      const confirmed = await confirmDestructive(
        'Enable data purge?',
        `“${config.key}” will permanently delete matching rows older than ${parsedDays} days on the daily 03:30 UTC run. This cannot be undone.`,
        'Enable purge',
      );
      if (!confirmed) return;
    }
    update.mutate(
      { key: config.key, enabled, retention_days: parsedDays },
      {
        onSuccess: () => showSuccess('Saved', `“${config.key}” updated.`),
        onError: (err) => showError('Save failed', getUserMessage(err)),
      },
    );
  };

  return (
    <View
      className="rounded-2xl border p-4 mb-3"
      style={{ backgroundColor: colors.surface.primary, borderColor: colors.border.light }}
    >
      {/* Key + enable toggle */}
      <View className="flex-row items-center justify-between">
        <Text
          style={{ color: colors.text.primary, fontFamily: 'monospace' }}
          className="text-base font-semibold flex-1"
          numberOfLines={1}
        >
          {config.key}
        </Text>
        <Switch
          value={enabled}
          onValueChange={setEnabled}
          trackColor={{ false: colors.border.medium, true: colors.primary[500] }}
        />
      </View>

      <Text style={{ color: colors.text.tertiary }} className="text-sm mt-1" numberOfLines={3}>
        {config.description}
      </Text>

      {/* Destructive-action note while enabled */}
      {enabled ? (
        <View
          className="flex-row items-center rounded-xl px-3 py-2 mt-3"
          style={{ backgroundColor: colors.traffic.amberLight }}
        >
          <AlertTriangle size={14} color={colors.traffic.amber} />
          <Text
            style={{ color: colors.traffic.amber }}
            className="text-xs ml-2 flex-1"
            numberOfLines={2}
          >
            Permanently deletes matching rows on the daily run. Deleted data cannot be recovered.
          </Text>
        </View>
      ) : null}

      {/* Retention window */}
      <View className="flex-row items-center mt-3">
        <Text style={{ color: colors.text.secondary }} className="text-sm flex-1" numberOfLines={1}>
          Retention window (days)
        </Text>
        <TextInput
          value={daysText}
          onChangeText={setDaysText}
          keyboardType="number-pad"
          autoCapitalize="none"
          autoCorrect={false}
          style={noOutlineStyle({
            color: colors.text.primary,
            backgroundColor: colors.surface.secondary,
            borderColor: daysValid ? colors.border.light : colors.traffic.red,
            width: 96,
            textAlign: 'right',
            fontFamily: 'monospace',
          })}
          className="rounded-xl border px-3 py-2 text-sm"
        />
      </View>
      {!daysValid ? (
        <Text style={{ color: colors.traffic.red }} className="text-xs mt-1">
          Must be a whole number greater than 0.
        </Text>
      ) : null}

      {/* Save */}
      <View className="flex-row justify-end mt-3">
        <AnimatedPressable
          onPress={handleSave}
          disabled={!dirty || update.isPending}
          className="px-5 py-2.5 rounded-xl"
          style={{
            backgroundColor: dirty ? colors.primary[600] : colors.surface.secondary,
            opacity: update.isPending ? 0.6 : 1,
          }}
        >
          <Text className="font-semibold" style={{ color: dirty ? colors.text.inverse : colors.text.tertiary }}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Text>
        </AnimatedPressable>
      </View>
    </View>
  );
}

/** Status chip for a run: success green / partial amber / failed red / running neutral. */
function RunStatusChip({ status }: { status: string }) {
  const colors = useThemeColors();
  const palette: Record<RetentionRunStatus, { fg: string; bg: string }> = {
    success: { fg: colors.traffic.green, bg: colors.traffic.greenLight },
    partial: { fg: colors.traffic.amber, bg: colors.traffic.amberLight },
    failed: { fg: colors.traffic.red, bg: colors.traffic.redLight },
    running: { fg: colors.text.secondary, bg: colors.surface.secondary },
  };
  const { fg, bg } = palette[status as RetentionRunStatus] ?? palette.running;
  return (
    <View className="rounded-full px-2.5 py-0.5" style={{ backgroundColor: bg }}>
      <Text className="text-xs font-semibold" style={{ color: fg }} numberOfLines={1}>
        {status}
      </Text>
    </View>
  );
}

/** One run in the history list — expandable to per-category results. */
function RunRow({ run }: { run: RetentionRun }) {
  const colors = useThemeColors();
  const [expanded, setExpanded] = useState(false);
  const results = parseRunResults(run.results);
  const Chevron = expanded ? ChevronUp : ChevronDown;

  return (
    <View
      className="rounded-2xl border mb-2 overflow-hidden"
      style={{ backgroundColor: colors.surface.primary, borderColor: colors.border.light }}
    >
      <AnimatedPressable onPress={() => setExpanded((v) => !v)} className="flex-row items-center p-3">
        <RunStatusChip status={run.status} />
        <View className="flex-1 ml-3">
          <Text style={{ color: colors.text.primary }} className="text-sm font-medium" numberOfLines={1}>
            {new Date(run.started_at).toLocaleString()}
          </Text>
          <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5" numberOfLines={1}>
            Triggered by {run.triggered_by}
            {run.finished_at ? '' : ' — in progress'}
          </Text>
        </View>
        <Chevron size={16} color={colors.text.tertiary} />
      </AnimatedPressable>

      {expanded ? (
        <View className="px-3 pb-3">
          {results.length === 0 ? (
            <Text style={{ color: colors.text.tertiary }} className="text-xs">
              No per-category results recorded yet.
            </Text>
          ) : (
            results.map((result) => (
              <View
                key={`result-${result.key}`}
                className="border-t py-2"
                style={{ borderTopColor: colors.border.light }}
              >
                <View className="flex-row items-center justify-between">
                  <Text
                    style={{ color: colors.text.primary, fontFamily: 'monospace' }}
                    className="text-xs flex-1"
                    numberOfLines={1}
                  >
                    {result.key}
                  </Text>
                  <Text
                    className="text-xs font-semibold ml-2"
                    style={{
                      color:
                        result.status === 'ok'
                          ? colors.traffic.green
                          : result.status === 'error'
                            ? colors.traffic.red
                            : colors.text.tertiary,
                    }}
                    numberOfLines={1}
                  >
                    {result.status === 'ok'
                      ? `deleted ${result.deleted ?? 0}`
                      : result.status}
                  </Text>
                </View>
                {typeof result.retention_days === 'number' ? (
                  <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5" numberOfLines={1}>
                    window: {result.retention_days} days
                  </Text>
                ) : null}
                {result.error ? (
                  // Verbatim SQLERRM — do not massage; the raw text is the diagnostic.
                  <Text style={{ color: colors.traffic.red, fontFamily: 'monospace' }} className="text-xs mt-1">
                    {result.error}
                  </Text>
                ) : null}
              </View>
            ))
          )}
        </View>
      ) : null}
    </View>
  );
}

export default function DataRetentionScreen() {
  const colors = useThemeColors();
  const { success: showSuccess, error: showError, confirmDestructive } = useAlert();
  const { data: configs, isLoading, isError } = useRetentionConfig();
  const { data: runs, isLoading: isRunsLoading, isError: isRunsError } = useRetentionRuns(20);
  const runNow = useRunRetentionNow();

  const handleRunNow = async () => {
    const enabledKeys = (configs ?? []).filter((c) => c.enabled).map((c) => c.key);
    const confirmed = await confirmDestructive(
      'Run retention cleanup now?',
      enabledKeys.length > 0
        ? `This permanently deletes rows for the enabled categories:\n${enabledKeys.join('\n')}`
        : 'No categories are enabled — the run will record every category as disabled and delete nothing.',
      'Run now',
    );
    if (!confirmed) return;
    runNow.mutate(undefined, {
      onSuccess: ({ runId }) => showSuccess('Retention run started', `Run ${runId} completed — see the run history below.`),
      onError: (err) => showError('Run failed', getUserMessage(err)),
    });
  };

  return (
    <AppLayout
      title="Data Retention"
      breadcrumbs={[
        { label: 'Admin', href: '/(admin)/admin-dashboard' },
        { label: 'Data Retention' },
      ]}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={isWeb ? [] : ['top', 'bottom']}>
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 32 }}>
          <View className="px-1 pt-2 pb-3">
            <View className="flex-row items-center">
              <Timer size={18} color={colors.text.primary} />
              <Text style={{ color: colors.text.primary }} className="text-xl font-bold ml-2">
                Data Retention
              </Text>
            </View>
            <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
              One purge category per row. Enabled categories permanently delete matching rows on the
              daily 03:30 UTC run — all categories ship disabled.
            </Text>
          </View>

          {isLoading ? (
            <View className="items-center justify-center py-12">
              <ActivityIndicator color={colors.primary[600]} />
            </View>
          ) : isError ? (
            <Text style={{ color: colors.traffic.red }} className="text-sm px-1">
              Couldn’t load retention config.
            </Text>
          ) : !configs || configs.length === 0 ? (
            <Text style={{ color: colors.text.tertiary }} className="text-sm px-1">
              No retention categories yet. Seed them via a migration (`public.retention_config`).
            </Text>
          ) : (
            configs.map((config) => <CategoryCard key={config.key} config={config} />)
          )}

          {/* Run history */}
          <View className="flex-row items-center justify-between px-1 pt-4 pb-2">
            <Text style={{ color: colors.text.primary }} className="text-lg font-bold" numberOfLines={1}>
              Recent runs
            </Text>
            <AnimatedPressable
              onPress={handleRunNow}
              disabled={runNow.isPending}
              className="flex-row items-center px-4 py-2 rounded-xl"
              style={{ backgroundColor: colors.primary[600], opacity: runNow.isPending ? 0.6 : 1 }}
            >
              <Play size={14} color={colors.text.inverse} />
              <Text className="text-sm font-semibold ml-1.5" style={{ color: colors.text.inverse }}>
                {runNow.isPending ? 'Running…' : 'Run now'}
              </Text>
            </AnimatedPressable>
          </View>

          {isRunsLoading ? (
            <View className="items-center justify-center py-8">
              <ActivityIndicator color={colors.primary[600]} />
            </View>
          ) : isRunsError ? (
            <Text style={{ color: colors.traffic.red }} className="text-sm px-1">
              Couldn’t load the run log.
            </Text>
          ) : !runs || runs.length === 0 ? (
            <Text style={{ color: colors.text.tertiary }} className="text-sm px-1">
              No runs yet. The daily job runs at 03:30 UTC, or trigger one with “Run now”.
            </Text>
          ) : (
            runs.map((run) => <RunRow key={run.id} run={run} />)
          )}
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
}
